import React, { useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { resolveMediaSrc } from '../lib/api';

/**
 * MenuModal — in-site popup that lists menu categories; clicking a category
 * downloads its uploaded menu (PDF or image) directly to the user's device.
 * CMS-driven via `menus` arrays of { label, fileUrl }.
 */
const MenuModal = ({ open, onClose, title, menus = [] }) => {
  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const items = (menus || []).filter((m) => m && m.label);

  const downloadMenu = (m) => {
    if (!m.fileUrl) return;
    const src = resolveMediaSrc(m.fileUrl);
    // GridFS-served files honor ?download=1 (Content-Disposition: attachment).
    const url = src.includes('/api/media/')
      ? `${src}${src.includes('?') ? '&' : '?'}download=1`
      : src;
    const a = document.createElement('a');
    a.href = url;
    a.download = m.label || 'menu';
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div data-testid="menu-modal" className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative bg-[#f7f1e3] w-full max-w-3xl max-h-[90vh] flex flex-col border border-[#942222]/30 shadow-2xl">
        <div className="flex items-center justify-between px-6 sm:px-8 py-4 border-b border-[#942222]/15 shrink-0">
          <h2 className="font-serif text-[#942222] text-2xl sm:text-3xl tracking-tight">{title}</h2>
          <button onClick={onClose} data-testid="menu-modal-close" className="text-[#942222] hover:opacity-70" aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-[200px]">
          <ul className="divide-y divide-[#942222]/10">
            {items.map((m, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => downloadMenu(m)}
                  disabled={!m.fileUrl}
                  data-testid={`menu-category-${i}`}
                  className={`w-full flex items-center justify-between px-6 sm:px-8 py-5 text-left transition-colors ${m.fileUrl ? 'hover:bg-[#efe6d0]' : 'opacity-50 cursor-not-allowed'}`}
                >
                  <span className="font-serif text-[#942222] text-2xl tracking-tight">{m.label}</span>
                  {m.fileUrl ? (
                    <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#942222]/70">
                      Download <Download size={18} className="text-[#942222]" />
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#942222]/60">Coming soon</span>
                  )}
                </button>
              </li>
            ))}
            {items.length === 0 && (
              <li className="px-8 py-20 text-center text-[#942222]/60 text-sm">Menu coming soon.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MenuModal;
